/**
 * Operator diagnostics pages — Incidents, Queues, Events, Providers, Costs,
 * Audit log and Feature flags. These mirror the product prototype but, like the
 * Overview page, render from clearly-LABELLED sample fixtures: none of these
 * surfaces has an operator endpoint yet (the §7 follow-ups). Every page carries
 * a persistent "preview" banner; the moment the endpoints ship, swap the
 * `SAMPLE_*` fixtures for the real hooks and drop the banners — the layout does
 * not change.
 *
 * HONESTY (spec rule #11): row data below is literal sample data, NOT i18n. The
 * chrome/copy comes from `useT('backoffice')`. The Costs / Events / Audit /
 * Feature-flag caveats are load-bearing and must stay.
 */

import { useT } from '@hostesa/i18n';
import {
  Card,
  cn,
  type LedgerColumn,
  LedgerTable,
  MetricCard,
  type PillTone,
  StatusPill,
  StatusTabs,
} from '@hostesa/ui';
import { ShieldAlert } from 'lucide-react';
import { type ReactNode, useState } from 'react';

// ───────────────────────────────────────────────────────────────────────────
// Shared typography + chrome (mirrors overview-page.tsx)
// ───────────────────────────────────────────────────────────────────────────

const EYEBROW =
  'font-[var(--font-mono)] text-[11px] uppercase tracking-wider text-[var(--color-stone-mid)]';
const LABEL =
  'font-[var(--font-mono)] text-[11px] uppercase tracking-wider text-[var(--color-stone-mid)]';
const VALUE = 'font-[var(--font-display)] text-lg text-[var(--color-charcoal)]';
const META = 'font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]';
const MONO = 'font-[var(--font-mono)] text-[13px] text-[var(--color-stone-700)]';

const SECTION_WRAP = 'mx-auto flex w-full max-w-6xl flex-col gap-6 px-8 py-8';

function DiagnosticsHeader({
  eyebrow,
  title,
  metric,
}: {
  eyebrow: string;
  title: string;
  metric?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex flex-col gap-1">
        <span className={EYEBROW}>{eyebrow}</span>
        <h1 className="font-[var(--font-display)] text-2xl text-[var(--color-charcoal)]">
          {title}
        </h1>
      </div>
      {metric}
    </header>
  );
}

/** Persistent honest "preview" banner — sample data until the operator endpoint ships. */
function PreviewNotice({ page, extra }: { page: string; extra?: string }) {
  const { t } = useT('backoffice');
  return (
    <div
      data-testid={`${page}-preview-notice`}
      role="note"
      className="flex items-start gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-warn-wash)] px-4 py-3"
    >
      <ShieldAlert className="mt-0.5 size-4 shrink-0 text-[var(--color-alert)]" />
      <p className="text-sm text-[var(--color-charcoal)]">
        <strong className="font-[var(--font-display)]">{t('diagnostics.preview.label')}</strong>{' '}
        <span className="text-[var(--color-stone-mid)]">{t('diagnostics.preview.body')}</span>
        {extra && (
          <>
            {' '}
            <span className="text-[var(--color-charcoal)]">{extra}</span>
          </>
        )}
      </p>
    </div>
  );
}

function FootNote({ testId, children }: { testId: string; children: ReactNode }) {
  return (
    <p data-testid={testId} className={cn(META, 'pt-1')}>
      {children}
    </p>
  );
}

function HeaderMetric({ testId, label }: { testId: string; label: string }) {
  return <StatusPill data-testid={testId} tone="alert" label={label} />;
}

const SEV_TONE: Record<string, PillTone> = { High: 'alert', Med: 'accent', Low: 'neutral' };

// ───────────────────────────────────────────────────────────────────────────
// 1. Incidents
// ───────────────────────────────────────────────────────────────────────────

interface IncidentRow {
  id: string;
  sev: 'High' | 'Med' | 'Low';
  title: string;
  age: string;
  owner: string;
  status: 'Open' | 'Investigating' | 'Monitoring';
}

const SAMPLE_INCIDENTS: IncidentRow[] = [
  {
    id: 'INC-204',
    sev: 'High',
    title: 'Elevated STT latency — eu-central',
    age: '12m',
    owner: 'on-call',
    status: 'Open',
  },
  {
    id: 'INC-203',
    sev: 'Med',
    title: 'Belmar Burgas line 6 not registering',
    age: '48m',
    owner: 'Petar K.',
    status: 'Open',
  },
  {
    id: 'INC-202',
    sev: 'Med',
    title: 'Sunny Coast Tower A — 1 line degraded',
    age: '1h 20m',
    owner: 'on-call',
    status: 'Investigating',
  },
  {
    id: 'INC-201',
    sev: 'Low',
    title: 'Mews webhook retries elevated',
    age: '2h',
    owner: 'Ivan D.',
    status: 'Investigating',
  },
  {
    id: 'INC-198',
    sev: 'High',
    title: 'Pirin Alpine both lines offline',
    age: '3h',
    owner: 'Petar K.',
    status: 'Open',
  },
  {
    id: 'INC-195',
    sev: 'Low',
    title: 'Knowledge crawl warnings — Bansko',
    age: '5h',
    owner: '—',
    status: 'Monitoring',
  },
];

const INCIDENT_FILTERS = { all: null, high: 'High', med: 'Med', low: 'Low' } as const;

export function IncidentsPage() {
  const { t } = useT('backoffice');
  const [tab, setTab] = useState<keyof typeof INCIDENT_FILTERS>('all');
  const sev = INCIDENT_FILTERS[tab];
  const rows = sev ? SAMPLE_INCIDENTS.filter((r) => r.sev === sev) : SAMPLE_INCIDENTS;
  const active = SAMPLE_INCIDENTS.filter((r) => r.status === 'Open' && r.sev === 'High').length;

  const columns: LedgerColumn<IncidentRow>[] = [
    {
      id: 'id',
      header: t('diagnostics.incidents.columns.id'),
      width: '90px',
      cell: (r) => <span className={MONO}>{r.id}</span>,
    },
    {
      id: 'severity',
      header: t('diagnostics.incidents.columns.severity'),
      width: '110px',
      cell: (r) => <StatusPill dot tone={SEV_TONE[r.sev]} label={r.sev} />,
    },
    {
      id: 'incident',
      header: t('diagnostics.incidents.columns.incident'),
      width: 'minmax(0,2fr)',
      cell: (r) => r.title,
    },
    {
      id: 'age',
      header: t('diagnostics.incidents.columns.age'),
      width: '80px',
      cell: (r) => <span className={META}>{r.age}</span>,
    },
    {
      id: 'owner',
      header: t('diagnostics.incidents.columns.owner'),
      width: '110px',
      cell: (r) => r.owner,
    },
    {
      id: 'status',
      header: t('diagnostics.incidents.columns.status'),
      width: '130px',
      cell: (r) => <StatusPill tone="neutral" label={r.status} />,
    },
  ];

  return (
    <section data-testid="incidents-page" className={SECTION_WRAP}>
      <DiagnosticsHeader
        eyebrow={t('navGroups.operations')}
        title={t('pages.incidents.title')}
        metric={
          <HeaderMetric
            testId="incidents-active-metric"
            label={t('diagnostics.incidents.activeMetric', { count: active })}
          />
        }
      />
      <PreviewNotice page="incidents" />
      <StatusTabs
        data-testid="incidents-tabs"
        value={tab}
        onChange={(id) => setTab(id as keyof typeof INCIDENT_FILTERS)}
        tabs={[
          { id: 'all', label: t('diagnostics.incidents.tabs.all') },
          { id: 'high', label: t('diagnostics.incidents.tabs.high') },
          { id: 'med', label: t('diagnostics.incidents.tabs.med') },
          { id: 'low', label: t('diagnostics.incidents.tabs.low') },
        ]}
      />
      <LedgerTable
        data-testid="incidents-table"
        rows={rows}
        getRowKey={(r) => r.id}
        columns={columns}
      />
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// 2. Queues
// ───────────────────────────────────────────────────────────────────────────

interface QueueRow {
  id: string;
  name: string;
  rate: string;
  failPct: string;
  depth: number;
  p95: string;
  dot: 'success' | 'alert' | 'neutral';
}

const SAMPLE_QUEUES: QueueRow[] = [
  {
    id: 'webhook-retry',
    name: 'webhook-retry',
    rate: '32/s',
    failPct: '4.1%',
    depth: 47,
    p95: '8.2s',
    dot: 'alert',
  },
  {
    id: 'transcription',
    name: 'transcription',
    rate: '88/s',
    failPct: '0.3%',
    depth: 6,
    p95: '1.1s',
    dot: 'success',
  },
  {
    id: 'reservation-sync',
    name: 'reservation-sync',
    rate: '14/s',
    failPct: '0.9%',
    depth: 3,
    p95: '740ms',
    dot: 'success',
  },
  {
    id: 'knowledge-crawl',
    name: 'knowledge-crawl',
    rate: '2/s',
    failPct: '1.7%',
    depth: 12,
    p95: '4.6s',
    dot: 'neutral',
  },
  {
    id: 'sms-dispatch',
    name: 'sms-dispatch',
    rate: '9/s',
    failPct: '0.1%',
    depth: 1,
    p95: '320ms',
    dot: 'success',
  },
  {
    id: 'email-dispatch',
    name: 'email-dispatch',
    rate: '21/s',
    failPct: '0.2%',
    depth: 2,
    p95: '410ms',
    dot: 'success',
  },
];

const SAMPLE_QUEUE_CARDS = [
  {
    id: 'webhook-retry',
    label: 'webhook-retry',
    value: '47',
    delta: 'backed up',
    tone: 'down' as const,
  },
  {
    id: 'transcription',
    label: 'transcription',
    value: '6',
    delta: 'healthy',
    tone: 'neutral' as const,
  },
  {
    id: 'reservation-sync',
    label: 'reservation-sync',
    value: '3',
    delta: 'healthy',
    tone: 'neutral' as const,
  },
  {
    id: 'knowledge-crawl',
    label: 'knowledge-crawl',
    value: '12',
    delta: '3 dead-letter',
    tone: 'down' as const,
  },
];

const QUEUE_DOT: Record<string, string> = {
  success: 'bg-[var(--color-success)]',
  alert: 'bg-[var(--color-alert)]',
  neutral: 'bg-[var(--color-stone-400)]',
};

export function QueuesPage() {
  const { t } = useT('backoffice');

  const columns: LedgerColumn<QueueRow>[] = [
    {
      id: 'queue',
      header: t('diagnostics.queues.columns.queue'),
      width: 'minmax(0,2fr)',
      cell: (r) => (
        <span className="flex items-center gap-2">
          <span className={cn('size-1.5 shrink-0 rounded-full', QUEUE_DOT[r.dot])} />
          <span className={MONO}>{r.name}</span>
        </span>
      ),
    },
    {
      id: 'rate',
      header: t('diagnostics.queues.columns.rate'),
      align: 'right',
      width: '90px',
      cell: (r) => r.rate,
    },
    {
      id: 'failPct',
      header: t('diagnostics.queues.columns.failPct'),
      align: 'right',
      width: '90px',
      cell: (r) => r.failPct,
    },
    {
      id: 'depth',
      header: t('diagnostics.queues.columns.depth'),
      align: 'right',
      width: '90px',
      cell: (r) => r.depth,
    },
    {
      id: 'p95',
      header: t('diagnostics.queues.columns.p95'),
      align: 'right',
      width: '90px',
      cell: (r) => r.p95,
    },
  ];

  return (
    <section data-testid="queues-page" className={SECTION_WRAP}>
      <DiagnosticsHeader
        eyebrow={t('navGroups.operations')}
        title={t('pages.queues.title')}
        metric={
          <HeaderMetric
            testId="queues-metric"
            label={t('diagnostics.queues.metric', { backedUp: 1, deadLetter: 3 })}
          />
        }
      />
      <PreviewNotice page="queues" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SAMPLE_QUEUE_CARDS.map((c) => (
          <MetricCard
            key={c.id}
            data-testid={`queues-card-${c.id}`}
            label={c.label}
            value={c.value}
            delta={{ value: c.delta, tone: c.tone }}
          />
        ))}
      </div>
      <LedgerTable
        data-testid="queues-table"
        rows={SAMPLE_QUEUES}
        getRowKey={(r) => r.id}
        columns={columns}
      />
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// 3. Events
// ───────────────────────────────────────────────────────────────────────────

interface EventRow {
  id: string;
  time: string;
  type: string;
  severity: 'Info' | 'Warning' | 'Error';
  tenant: string;
  payload: string;
}

const SAMPLE_EVENTS: EventRow[] = [
  {
    id: 'e1',
    time: '21:11:04',
    type: 'reservation.drafted',
    severity: 'Info',
    tenant: 'Sofia City Hotels',
    payload: '{ "hotel": "city-central", "nights": 3 }',
  },
  {
    id: 'e2',
    time: '21:09:55',
    type: 'integration.sync.ok',
    severity: 'Info',
    tenant: 'Vila Rila Group',
    payload: '{ "pms": "mews", "synced": 142 }',
  },
  {
    id: 'e3',
    time: '21:08:12',
    type: 'webhook.failed',
    severity: 'Warning',
    tenant: 'Sunny Coast Group',
    payload: '{ "status": 502, "retries": 4 }',
  },
  {
    id: 'e4',
    time: '21:06:41',
    type: 'handoff.requested',
    severity: 'Info',
    tenant: 'Black Sea Resorts',
    payload: '{ "reason": "guest_request" }',
  },
  {
    id: 'e5',
    time: '21:04:18',
    type: 'call.failed',
    severity: 'Error',
    tenant: 'Pirin Alpine',
    payload: '{ "code": "sip_408", "line": 2 }',
  },
  {
    id: 'e6',
    time: '21:01:50',
    type: 'knowledge.crawl.completed',
    severity: 'Info',
    tenant: 'Rila Retreats',
    payload: '{ "pages": 38, "warnings": 2 }',
  },
  {
    id: 'e7',
    time: '20:58:33',
    type: 'webhook.failed',
    severity: 'Warning',
    tenant: 'Varna Bay Collection',
    payload: '{ "status": 500, "retries": 1 }',
  },
  {
    id: 'e8',
    time: '20:55:07',
    type: 'integration.sync.ok',
    severity: 'Info',
    tenant: 'Plovdiv Old Town Hotels',
    payload: '{ "pms": "apaleo", "synced": 64 }',
  },
  {
    id: 'e9',
    time: '20:51:22',
    type: 'call.failed',
    severity: 'Error',
    tenant: 'Demo Hotel Group',
    payload: '{ "code": "no_answer", "line": 1 }',
  },
];

const EVENT_FILTERS = { all: null, info: 'Info', warning: 'Warning', error: 'Error' } as const;
const EVENT_SEV_TONE: Record<string, PillTone> = {
  Info: 'neutral',
  Warning: 'accent',
  Error: 'alert',
};

export function EventsPage() {
  const { t } = useT('backoffice');
  const [tab, setTab] = useState<keyof typeof EVENT_FILTERS>('all');
  const sev = EVENT_FILTERS[tab];
  const rows = sev ? SAMPLE_EVENTS.filter((r) => r.severity === sev) : SAMPLE_EVENTS;

  const columns: LedgerColumn<EventRow>[] = [
    {
      id: 'time',
      header: t('diagnostics.events.columns.time'),
      width: '90px',
      cell: (r) => <span className={MONO}>{r.time}</span>,
    },
    {
      id: 'type',
      header: t('diagnostics.events.columns.type'),
      width: '220px',
      cell: (r) => (
        <span className="inline-flex items-center gap-2">
          <StatusPill tone={EVENT_SEV_TONE[r.severity]} label={r.severity} />
          <span className={MONO}>{r.type}</span>
        </span>
      ),
    },
    {
      id: 'tenant',
      header: t('diagnostics.events.columns.tenant'),
      width: 'minmax(0,1fr)',
      cell: (r) => r.tenant,
    },
    {
      id: 'payload',
      header: t('diagnostics.events.columns.payload'),
      width: 'minmax(0,1.4fr)',
      cell: (r) => <span className={MONO}>{r.payload}</span>,
    },
  ];

  return (
    <section data-testid="events-page" className={SECTION_WRAP}>
      <DiagnosticsHeader eyebrow={t('navGroups.operations')} title={t('pages.events.title')} />
      <PreviewNotice page="events" />
      <StatusTabs
        data-testid="events-tabs"
        value={tab}
        onChange={(id) => setTab(id as keyof typeof EVENT_FILTERS)}
        tabs={[
          { id: 'all', label: t('diagnostics.events.tabs.all') },
          { id: 'info', label: t('diagnostics.events.tabs.info') },
          { id: 'warning', label: t('diagnostics.events.tabs.warning') },
          { id: 'error', label: t('diagnostics.events.tabs.error') },
        ]}
      />
      <LedgerTable
        data-testid="events-table"
        rows={rows}
        getRowKey={(r) => r.id}
        columns={columns}
      />
      <FootNote testId="events-footer-note">{t('diagnostics.events.footnote')}</FootNote>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// 4. Providers
// ───────────────────────────────────────────────────────────────────────────

interface ProviderCard {
  id: string;
  label: string;
  operational: boolean;
  uptime: string;
  p95: string;
  errRate: string;
  sessions: string;
}

const SAMPLE_PROVIDERS: ProviderCard[] = [
  {
    id: 'vapi',
    label: 'Vapi',
    operational: true,
    uptime: '99.98%',
    p95: '310ms',
    errRate: '0.1%',
    sessions: '1204',
  },
  {
    id: 'livekit',
    label: 'LiveKit',
    operational: true,
    uptime: '99.99%',
    p95: '88ms',
    errRate: '0.0%',
    sessions: '980',
  },
  {
    id: 'deepgram',
    label: 'Deepgram',
    operational: false,
    uptime: '99.7%',
    p95: '410ms',
    errRate: '0.6%',
    sessions: '540',
  },
  {
    id: 'openai',
    label: 'OpenAI',
    operational: true,
    uptime: '99.95%',
    p95: '620ms',
    errRate: '0.2%',
    sessions: '720',
  },
];

function ProviderStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className={LABEL}>{label}</span>
      <span className="font-[var(--font-mono)] text-sm text-[var(--color-charcoal)]">{value}</span>
    </div>
  );
}

export function ProvidersPage() {
  const { t } = useT('backoffice');
  const degraded = SAMPLE_PROVIDERS.filter((p) => !p.operational).length;

  return (
    <section data-testid="providers-page" className={SECTION_WRAP}>
      <DiagnosticsHeader
        eyebrow={t('navGroups.diagnostics')}
        title={t('pages.providers.title')}
        metric={
          <HeaderMetric
            testId="providers-degraded-metric"
            label={t('diagnostics.providers.degradedMetric', { count: degraded })}
          />
        }
      />
      {/* Internal-only provider-metadata notice (the ONLY place provider metadata appears). */}
      <div
        data-testid="provider-metadata-notice"
        role="note"
        className="rounded-md border border-[var(--color-border)] bg-[var(--color-alabaster)] px-4 py-3 text-sm text-[var(--color-stone-mid)]"
      >
        <strong className="font-[var(--font-display)] text-[var(--color-charcoal)]">
          {t('providerNotice.label')}
        </strong>
        <p className="mt-1">{t('providerNotice.body')}</p>
      </div>
      <PreviewNotice page="providers" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {SAMPLE_PROVIDERS.map((p) => (
          <Card
            key={p.id}
            data-testid={`providers-card-${p.id}`}
            className="flex flex-col gap-4 p-5"
          >
            <div className="flex items-center justify-between">
              <span className={VALUE}>{p.label}</span>
              <StatusPill
                dot
                tone={p.operational ? 'success' : 'alert'}
                label={
                  p.operational
                    ? t('diagnostics.providers.status.operational')
                    : t('diagnostics.providers.status.elevated')
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <ProviderStat label={t('diagnostics.providers.stats.uptime')} value={p.uptime} />
              <ProviderStat label={t('diagnostics.providers.stats.p95')} value={p.p95} />
              <ProviderStat label={t('diagnostics.providers.stats.errRate')} value={p.errRate} />
              <ProviderStat label={t('diagnostics.providers.stats.sessions')} value={p.sessions} />
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// 5. Costs
// ───────────────────────────────────────────────────────────────────────────

interface CostRow {
  id: string;
  tenant: string;
  stt: string;
  llm: string;
  telephony: string;
  totalMtd: string;
}

const SAMPLE_COSTS: CostRow[] = [
  {
    id: 'vila-rila',
    tenant: 'Vila Rila Group',
    stt: '8.1k',
    llm: '3.0M',
    telephony: '€260',
    totalMtd: '€1,040',
  },
  {
    id: 'black-sea',
    tenant: 'Black Sea Resorts',
    stt: '6.4k',
    llm: '2.4M',
    telephony: '€210',
    totalMtd: '€820',
  },
  {
    id: 'sofia-city',
    tenant: 'Sofia City Hotels',
    stt: '5.2k',
    llm: '1.9M',
    telephony: '€170',
    totalMtd: '€660',
  },
  {
    id: 'plovdiv',
    tenant: 'Plovdiv Old Town Hotels',
    stt: '4.1k',
    llm: '1.5M',
    telephony: '€130',
    totalMtd: '€520',
  },
  {
    id: 'rila-retreats',
    tenant: 'Rila Retreats',
    stt: '3.6k',
    llm: '1.3M',
    telephony: '€110',
    totalMtd: '€450',
  },
  {
    id: 'varna-bay',
    tenant: 'Varna Bay Collection',
    stt: '3.1k',
    llm: '1.1M',
    telephony: '€95',
    totalMtd: '€390',
  },
  {
    id: 'sunny-coast',
    tenant: 'Sunny Coast Group',
    stt: '4.8k',
    llm: '1.8M',
    telephony: '€140',
    totalMtd: '€620',
  },
  {
    id: 'demo',
    tenant: 'Demo Hotel Group',
    stt: '2.9k',
    llm: '1.1M',
    telephony: '€25',
    totalMtd: '€320',
  },
];

const SAMPLE_COST_CARDS = [
  { id: 'stt', label: 'STT minutes', value: '38.2k', delta: '+2%', tone: 'neutral' as const },
  { id: 'llm', label: 'LLM tokens', value: '14.1M', delta: '+5%', tone: 'neutral' as const },
  { id: 'telephony', label: 'Telephony', value: '€1,240', delta: '-1%', tone: 'up' as const },
  {
    id: 'cost-per-call',
    label: 'Cost / call',
    value: '€0.16',
    delta: '+€0.01',
    tone: 'down' as const,
  },
];

export function CostsPage() {
  const { t } = useT('backoffice');

  const columns: LedgerColumn<CostRow>[] = [
    {
      id: 'tenant',
      header: t('diagnostics.costs.columns.tenant'),
      width: 'minmax(0,2fr)',
      cell: (r) => r.tenant,
    },
    {
      id: 'stt',
      header: t('diagnostics.costs.columns.stt'),
      align: 'right',
      width: '90px',
      cell: (r) => r.stt,
    },
    {
      id: 'llm',
      header: t('diagnostics.costs.columns.llm'),
      align: 'right',
      width: '90px',
      cell: (r) => r.llm,
    },
    {
      id: 'telephony',
      header: t('diagnostics.costs.columns.telephony'),
      align: 'right',
      width: '100px',
      cell: (r) => r.telephony,
    },
    {
      id: 'totalMtd',
      header: t('diagnostics.costs.columns.totalMtd'),
      align: 'right',
      width: '110px',
      cell: (r) => r.totalMtd,
    },
  ];

  return (
    <section data-testid="costs-page" className={SECTION_WRAP}>
      <DiagnosticsHeader
        eyebrow={t('navGroups.platform')}
        title={t('pages.costs.title')}
        metric={
          <HeaderMetric
            testId="costs-metric"
            label={t('diagnostics.costs.mtdMetric', { value: '€4,820' })}
          />
        }
      />
      <PreviewNotice page="costs" extra={t('diagnostics.costs.billingCaveat')} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SAMPLE_COST_CARDS.map((c) => (
          <MetricCard
            key={c.id}
            data-testid={`costs-card-${c.id}`}
            label={c.label}
            value={c.value}
            delta={{ value: c.delta, tone: c.tone }}
          />
        ))}
      </div>
      <LedgerTable
        data-testid="costs-table"
        rows={SAMPLE_COSTS}
        getRowKey={(r) => r.id}
        columns={columns}
      />
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// 6. Audit log
// ───────────────────────────────────────────────────────────────────────────

interface AuditRow {
  id: string;
  time: string;
  actor: string;
  action: string;
  resource: string;
  risk: 'High' | 'Med' | 'Low';
  outcome: 'success' | 'denied';
}

const SAMPLE_AUDIT: AuditRow[] = [
  {
    id: 'a1',
    time: '21:09:55',
    actor: 'Ivan Dimitrov · C-8809',
    action: 'pii.phone.reveal',
    resource: 'Sofia City Hotels',
    risk: 'High',
    outcome: 'success',
  },
  {
    id: 'a2',
    time: '20:54:12',
    actor: 'Nadia Koleva · HOS-4830',
    action: 'admin.impersonate.started',
    resource: 'Vila Rila Group',
    risk: 'High',
    outcome: 'success',
  },
  {
    id: 'a3',
    time: '20:41:30',
    actor: 'Petar K. · C-8801',
    action: 'feature_flag.update',
    resource: 'cost_caps_hard',
    risk: 'High',
    outcome: 'success',
  },
  {
    id: 'a4',
    time: '20:33:18',
    actor: 'unknown · 91.0.4.22',
    action: 'auth.login',
    resource: 'operator-console',
    risk: 'Med',
    outcome: 'denied',
  },
  {
    id: 'a5',
    time: '20:31:07',
    actor: 'Petar K. · C-8801',
    action: 'integration.credentials.rotated',
    resource: 'Sunny Coast Group',
    risk: 'High',
    outcome: 'success',
  },
  {
    id: 'a6',
    time: '20:22:44',
    actor: 'Ivan Dimitrov · C-8809',
    action: 'pii.email.reveal',
    resource: 'Black Sea Resorts',
    risk: 'High',
    outcome: 'success',
  },
  {
    id: 'a7',
    time: '20:14:09',
    actor: 'Nadia Koleva · HOS-4830',
    action: 'data.export.requested',
    resource: 'cross-tenant-calls',
    risk: 'Med',
    outcome: 'success',
  },
  {
    id: 'a8',
    time: '20:02:51',
    actor: 'Nadia Koleva · HOS-4830',
    action: 'admin.impersonate.ended',
    resource: 'Vila Rila Group',
    risk: 'Low',
    outcome: 'success',
  },
];

const AUDIT_FILTERS = { all: null, high: 'High', medium: 'Med', low: 'Low' } as const;
const RISK_TONE: Record<string, PillTone> = { High: 'alert', Med: 'accent', Low: 'neutral' };

export function AuditLogPage() {
  const { t } = useT('backoffice');
  const [tab, setTab] = useState<keyof typeof AUDIT_FILTERS>('all');
  const risk = AUDIT_FILTERS[tab];
  const rows = risk ? SAMPLE_AUDIT.filter((r) => r.risk === risk) : SAMPLE_AUDIT;

  const columns: LedgerColumn<AuditRow>[] = [
    {
      id: 'time',
      header: t('diagnostics.audit.columns.time'),
      width: '90px',
      cell: (r) => <span className={MONO}>{r.time}</span>,
    },
    {
      id: 'actor',
      header: t('diagnostics.audit.columns.actor'),
      width: 'minmax(0,1.4fr)',
      cell: (r) => r.actor,
    },
    {
      id: 'action',
      header: t('diagnostics.audit.columns.action'),
      width: 'minmax(0,1.4fr)',
      cell: (r) => <span className={MONO}>{r.action}</span>,
    },
    {
      id: 'resource',
      header: t('diagnostics.audit.columns.resource'),
      width: 'minmax(0,1fr)',
      cell: (r) => r.resource,
    },
    {
      id: 'risk',
      header: t('diagnostics.audit.columns.risk'),
      width: '90px',
      cell: (r) => <StatusPill tone={RISK_TONE[r.risk]} label={r.risk} />,
    },
    {
      id: 'outcome',
      header: t('diagnostics.audit.columns.outcome'),
      width: '110px',
      cell: (r) => (
        <StatusPill dot tone={r.outcome === 'success' ? 'success' : 'alert'} label={r.outcome} />
      ),
    },
  ];

  return (
    <section data-testid="audit-log-page" className={SECTION_WRAP}>
      <DiagnosticsHeader eyebrow={t('navGroups.governance')} title={t('pages.auditLog.title')} />
      <PreviewNotice page="audit-log" />
      <StatusTabs
        data-testid="audit-log-tabs"
        value={tab}
        onChange={(id) => setTab(id as keyof typeof AUDIT_FILTERS)}
        tabs={[
          { id: 'all', label: t('diagnostics.audit.tabs.all') },
          { id: 'high', label: t('diagnostics.audit.tabs.high') },
          { id: 'medium', label: t('diagnostics.audit.tabs.medium') },
          { id: 'low', label: t('diagnostics.audit.tabs.low') },
        ]}
      />
      <LedgerTable
        data-testid="audit-log-table"
        rows={rows}
        getRowKey={(r) => r.id}
        columns={columns}
      />
      <FootNote testId="audit-footer-note">{t('diagnostics.audit.footnote')}</FootNote>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// 7. Feature flags
// ───────────────────────────────────────────────────────────────────────────

interface FlagRow {
  id: string;
  name: string;
  risk: 'High' | 'Med' | 'Low';
  rollout: string;
  scope: string;
  owner: string;
  state: 'On' | 'Off' | 'Partial';
}

const SAMPLE_FLAGS: FlagRow[] = [
  {
    id: 'handoff_v2',
    name: 'handoff_v2',
    risk: 'High',
    rollout: '100%',
    scope: 'All tenants',
    owner: 'Ivan D.',
    state: 'On',
  },
  {
    id: 'multilang_es_el',
    name: 'multilang_es_el',
    risk: 'Med',
    rollout: '100%',
    scope: 'All tenants',
    owner: 'Nadia K.',
    state: 'On',
  },
  {
    id: 'cost_caps_hard',
    name: 'cost_caps_hard',
    risk: 'High',
    rollout: '25%',
    scope: 'All tenants',
    owner: 'Petar K.',
    state: 'Partial',
  },
  {
    id: 'realtime_v3_gateway',
    name: 'realtime_v3_gateway',
    risk: 'High',
    rollout: 'off',
    scope: 'Staging',
    owner: 'on-call',
    state: 'Off',
  },
  {
    id: 'pms_apaleo_connector',
    name: 'pms_apaleo_connector',
    risk: 'Med',
    rollout: '100%',
    scope: 'All tenants',
    owner: 'Ivan D.',
    state: 'On',
  },
  {
    id: 'transcript_redaction_el',
    name: 'transcript_redaction_el',
    risk: 'Low',
    rollout: '100%',
    scope: 'All tenants',
    owner: 'Nadia K.',
    state: 'On',
  },
];

const FLAG_STATE_TONE: Record<string, PillTone> = {
  On: 'success',
  Off: 'neutral',
  Partial: 'accent',
};

export function FeatureFlagsPage() {
  const { t } = useT('backoffice');
  const production = SAMPLE_FLAGS.filter((f) => f.scope === 'All tenants').length;
  const staging = SAMPLE_FLAGS.filter((f) => f.scope === 'Staging').length;

  const columns: LedgerColumn<FlagRow>[] = [
    {
      id: 'flag',
      header: t('diagnostics.featureFlags.columns.flag'),
      width: 'minmax(0,2fr)',
      cell: (r) => <span className={MONO}>{r.name}</span>,
    },
    {
      id: 'risk',
      header: t('diagnostics.featureFlags.columns.risk'),
      width: '90px',
      cell: (r) => <StatusPill tone={RISK_TONE[r.risk]} label={r.risk} />,
    },
    {
      id: 'rollout',
      header: t('diagnostics.featureFlags.columns.rollout'),
      align: 'right',
      width: '90px',
      cell: (r) => r.rollout,
    },
    {
      id: 'scope',
      header: t('diagnostics.featureFlags.columns.scope'),
      width: '120px',
      cell: (r) => r.scope,
    },
    {
      id: 'owner',
      header: t('diagnostics.featureFlags.columns.owner'),
      width: '110px',
      cell: (r) => r.owner,
    },
    {
      id: 'state',
      header: t('diagnostics.featureFlags.columns.state'),
      width: '100px',
      cell: (r) => <StatusPill dot tone={FLAG_STATE_TONE[r.state]} label={r.state} />,
    },
  ];

  return (
    <section data-testid="feature-flags-page" className={SECTION_WRAP}>
      <DiagnosticsHeader
        eyebrow={t('navGroups.governance')}
        title={t('pages.featureFlags.title')}
        metric={
          <HeaderMetric
            testId="feature-flags-metric"
            label={t('diagnostics.featureFlags.metric', { production, staging })}
          />
        }
      />
      <PreviewNotice page="feature-flags" extra={t('diagnostics.featureFlags.caveat')} />
      <LedgerTable
        data-testid="feature-flags-table"
        rows={SAMPLE_FLAGS}
        getRowKey={(r) => r.id}
        columns={columns}
      />
    </section>
  );
}
